import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { questsApi } from '../quests/api';
import type { PlayerQuest } from '../quests/types';

export function QuestsSection() {
  const qc = useQueryClient();
  const quests = useQuery<{ quests: PlayerQuest[] }>({
    queryKey: ['me-quests'],
    queryFn: () => questsApi.myQuests(),
  });

  const claim = useMutation({
    mutationFn: (id: string) => questsApi.claim(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-quests'] }),
  });
  const unclaim = useMutation({
    mutationFn: (id: string) => questsApi.unclaim(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-quests'] }),
  });
  // M5.15: complete response now returns total_xp + total_coins
  // + splits (per-competency breakdown). The UI shows the totals
  // in the success toast and the breakdown when there are
  // multiple splits, so the player sees the actual reward shape
  // (XP + coins per competency) rather than a single number.
  const complete = useMutation<{ ok: true; status: 'completed' | 'pending_review'; total_xp?: number; total_coins?: number; splits?: Array<{ competency_id: string; xp: number; coins?: number }> }, Error, string>({
    mutationFn: (id: string) => questsApi.complete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me-quests'] });
      qc.invalidateQueries({ queryKey: ['me-progress'] });
      qc.invalidateQueries({ queryKey: ['me-events'] });
    },
  });

  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  if (quests.isLoading) return <p className="text-ink-muted">Loading quests…</p>;
  const list = quests.data?.quests ?? [];

  const active = list.filter((q) => q.my_status === 'claimed' || q.my_status === 'pending_review');
  const available = list.filter((q) => q.my_status === 'available' || q.my_status === null);

  return (
    <div className="space-y-4">
      {feedback ? (
        <div
          className={feedback.kind === 'ok' ? 'rounded-md border-l-4 border-l-accent-success bg-bg-elevated p-3 text-sm' : 'rounded-md border-l-4 border-l-accent-danger bg-bg-elevated p-3 text-sm'}
          role="status"
        >
          {feedback.text}
          <button className="btn ml-3" onClick={() => setFeedback(null)}>Dismiss</button>
        </div>
      ) : null}

      {active.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Your active quests</h3>
          {active.map((q) => (
            <QuestCard
              key={q.id}
              quest={q}
              showActions
              onClaim={() => {}}
              onUnclaim={async () => {
                setFeedback(null);
                try { await unclaim.mutateAsync(q.id); setFeedback({ kind: 'ok', text: 'Quest returned to the board.' }); }
                catch (e) { setFeedback({ kind: 'err', text: e instanceof ApiError ? e.message : 'Unclaim failed.' }); }
              }}
              onComplete={async () => {
                setFeedback(null);
                try {
                  const r = await complete.mutateAsync(q.id);
                  if (r.status === 'pending_review') {
                    setFeedback({ kind: 'ok', text: 'Marked done. An admin will review and award XP.' });
                  } else if (r.splits && r.splits.length > 1) {
                    // Multi-competency quest: show total
                    // + breakdown so the player sees which
                    // competencies moved. Single-split
                    // quests get the short toast (matches
                    // the pre-M5.15 wording).
                    const xp = r.total_xp ?? 0;
                    const coins = r.total_coins ?? 0;
                    const parts = r.splits.map((s) => {
                      const coinStr = (s.coins ?? 0) > 0 ? `+${s.coins}🪙` : '';
                      return `+${s.xp}${coinStr ? ` ${coinStr}` : ''} ${s.competency_id}`;
                    }).join(', ');
                    const coinPart = coins > 0 ? ` + ${coins} 🪙` : '';
                    setFeedback({ kind: 'ok', text: `Completed! ${xp} XP${coinPart} across ${r.splits.length} competencies: ${parts}.` });
                  } else {
                    // Single split (the common case)
                    const xp = r.total_xp ?? 0;
                    const coins = r.total_coins ?? 0;
                    const coinPart = coins > 0 ? ` + ${coins} 🪙` : '';
                    setFeedback({ kind: 'ok', text: `Completed! Awarded ${xp} XP${coinPart}.` });
                  }
                } catch (e) {
                  setFeedback({ kind: 'err', text: e instanceof ApiError ? e.message : 'Complete failed.' });
                }
              }}
            />
          ))}
        </div>
      ) : null}

      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Open quests</h3>
          <Link to="/quests" className="text-xs text-ink-muted hover:text-ink">Public board →</Link>
        </div>
        {available.length === 0 ? (
          <p className="rounded-md bg-bg-elevated p-3 text-sm text-ink-muted">
            No quests open right now. Check back later, or look at the <Link to="/quests" className="underline">public board</Link>.
          </p>
        ) : (
          <div className="space-y-2">
            {available.slice(0, 5).map((q) => (
              <QuestCard
                key={q.id}
                quest={q}
                onClaim={async () => {
                  setFeedback(null);
                  try { await claim.mutateAsync(q.id); setFeedback({ kind: 'ok', text: 'Quest claimed. Mark it done when you finish.' }); }
                  catch (e) { setFeedback({ kind: 'err', text: e instanceof ApiError ? e.message : 'Claim failed.' }); }
                }}
              />
            ))}
            {available.length > 5 ? (
              <p className="text-xs text-ink-muted">+ {available.length - 5} more on the <Link to="/quests" className="underline">public board</Link>.</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function QuestCard({
  quest,
  onClaim,
  onUnclaim,
  onComplete,
  showActions,
}: {
  quest: PlayerQuest;
  onClaim: () => void;
  onUnclaim?: () => void;
  onComplete?: () => void;
  showActions?: boolean;
}) {
  const myStatus = quest.my_status;
  const action = (() => {
    if (myStatus === 'available' || myStatus === null) {
      return <button className="btn-primary" onClick={onClaim}>Claim</button>;
    }
    if (myStatus === 'claimed' && showActions) {
      return (
        <div className="flex gap-2">
          <button className="btn" onClick={onUnclaim}>Drop</button>
          <button className="btn-primary" onClick={onComplete}>Mark done</button>
        </div>
      );
    }
    if (myStatus === 'pending_review' && showActions) {
      return <span className="text-xs text-ink-dim">Awaiting admin review</span>;
    }
    if (myStatus === 'completed') {
      // M5.10: for repeatable quests, show "Repeat
      // quest" instead of the "Completed ✓" static
      // label. The button triggers a new claim, which
      // the server will accept if (a) the user has no
      // in-flight attempt, and (b) the global
      // max_completions cap hasn't been hit. The
      // attempt count is shown so the user can see
      // how many times they've done this.
      if (quest.repeatable && quest.claim_count < quest.max_completions) {
        return (
          <div className="flex flex-col items-end gap-1">
            <button className="btn-primary" onClick={onClaim}>
              Repeat quest
            </button>
            <span className="text-xs text-ink-dim">
              attempt {quest.my_attempt ?? 1} done
            </span>
          </div>
        );
      }
      return <span className="text-xs text-accent-success">Completed ✓</span>;
    }
    return null;
  })();

  return (
    <div className="rounded-md bg-bg-elevated p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <h4 className="font-medium">{quest.title}</h4>
            {/*
              M5.15: the headline XP is the sum of all
              splits. Multi-split quests show "+N XP" as
              the headline and a small chip with the
              per-competency count so the player knows
              the reward is spread across competencies
              without crowding the title row. Empty
              splits (ceremonial quest) shows "no XP".
              Coins (when >0) show next to XP as
              "+N 🪙" — same color as the existing coin
              chip on the dashboard so the visual
              language matches.
            */}
            {quest.splits.length === 0 ? (
              <span className="text-xs text-ink-dim">no XP</span>
            ) : (
              <>
                <span className="font-mono text-xs text-accent-success">
                  +{quest.splits.reduce((s, x) => s + x.xp, 0)} XP
                </span>
                {quest.splits.reduce((s, x) => s + (x.coins ?? 0), 0) > 0 ? (
                  <span className="font-mono text-xs text-accent-warning">
                    +{quest.splits.reduce((s, x) => s + (x.coins ?? 0), 0)} 🪙
                  </span>
                ) : null}
                {quest.splits.length > 1 ? (
                  <span className="rounded-full bg-bg-panel px-2 py-0.5 text-xs text-ink-dim">
                    {quest.splits.length} competencies
                  </span>
                ) : null}
              </>
            )}
            {quest.due_at ? (
              <span className="text-xs text-ink-dim">due {new Date(quest.due_at).toLocaleDateString()}</span>
            ) : null}
          </div>
          {quest.description ? (
            <p className="mt-1 text-sm text-ink-muted">{quest.description}</p>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-dim">
            <span className="rounded-full bg-bg-panel px-2 py-0.5">{quest.mode}</span>
            {quest.review_mode === 'review' ? (
              <span className="rounded-full bg-bg-panel px-2 py-0.5">review required</span>
            ) : null}
            {/* M5.10: surface the repeatable flag here
                so players know they can do this quest
                again. Small but unmistakable. */}
            {quest.repeatable ? (
              <span className="rounded-full bg-bg-panel px-2 py-0.5">repeatable</span>
            ) : null}
            {quest.mode === 'open' ? (
              <span>{quest.claim_count}/{quest.max_completions} claimed</span>
            ) : null}
          </div>
        </div>
        <div className="flex-shrink-0">{action}</div>
      </div>
    </div>
  );
}
